/**
 * Formatting helpers ported from the design mockup's `data.js`, so dates,
 * initials and greetings read identically to `Visual - Demo.dc.html`.
 *
 * Dates are handled as bare ISO days (`"2026-06-05"`) and always parsed as UTC,
 * which keeps a date from sliding a day either way depending on the viewer's
 * timezone.
 */

/**
 * Whose words a date is rendered in: **the reader's**.
 *
 * `undefined` is what `toLocaleDateString` takes to mean "the device's locale",
 * and it is exported under a name for one reason — an argument that is simply
 * absent cannot be told from one somebody forgot. This is a decision.
 *
 * The console hardcoded `en-GB` at sixteen call sites while the phone had always
 * used the device's, so one stored day read two ways depending on which screen
 * you were looking at. Confirmed with the product owner 2026-08-17: the reader's
 * locale wins, because the four jurisdictions this product models are CZ, UK, PL
 * and DE, and a Czech site manager reading a Czech date on the phone should not
 * meet an English one in the office.
 *
 * **Two things this deliberately does not touch.** The `timeZone: "UTC"` beside
 * it is not a locale choice and stays — a bare ISO day parsed in local time
 * slides a day either way depending on where the viewer is, which is a bug in
 * any language. And the roster CSV emits raw `YYYY-MM-DD`, unformatted, so a
 * spreadsheet cell never varies by whoever exported it.
 */
export const DISPLAY_LOCALE: string | undefined = undefined;

const MS_PER_DAY = 86_400_000;

function utc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/** Shift an ISO day by `n` days, returning another ISO day. */
export function addDays(iso: string, n: number): string {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** `"2026-02-15"` → `"15 Feb 2026"`. */
export function formatDate(iso: string): string {
  return utc(iso).toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * A moment, as the day it fell on where the reader is:
 * `"2026-09-22T20:29:58.736Z"` → `"22 Sept 2026"`.
 *
 * Not `formatDate`, which takes a bare day and reads it in UTC. A timestamp
 * handed to that one renders "Invalid Date" (found on the import review,
 * 2026-09-22). Anything unparseable is shown as it came.
 */
export function formatDayOf(timestamp: string): string {
  const moment = new Date(timestamp);
  if (Number.isNaN(moment.getTime())) return timestamp;
  return moment.toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * A moment as a person reads it, where they are: the time alone when it fell
 * today (`"10:42"`), and the day with the time otherwise (`"15 Oct, 10:42"`).
 * For "Last checked …" lines, where today is the usual answer. Anything
 * unparseable is shown as it came.
 */
export function formatMoment(timestamp: string, now: Date = new Date()): string {
  const moment = new Date(timestamp);
  if (Number.isNaN(moment.getTime())) return timestamp;
  const time = moment.toLocaleTimeString(DISPLAY_LOCALE, { hour: "2-digit", minute: "2-digit" });
  if (moment.toDateString() === now.toDateString()) return time;
  const day = moment.toLocaleDateString(DISPLAY_LOCALE, { day: "numeric", month: "short" });
  return `${day}, ${time}`;
}

/** `"2026-02-15"` → `"15 Feb"`. */
export function formatDateShort(iso: string): string {
  return utc(iso).toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** Whole days from `from` to `iso` — negative when `iso` is in the past. */
export function daysBetween(iso: string, from: string): number {
  return Math.round((utc(iso).getTime() - utc(from).getTime()) / MS_PER_DAY);
}

/** `"Lucie Dvořáková"` → `"LD"`; at most two letters. */
export function initialsOf(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => (part[0] ?? "").toUpperCase())
    .slice(0, 2)
    .join("");
}

/** The name shown in the greeting — first word only. */
export function firstName(name: string): string {
  return name.split(" ")[0] ?? name;
}

/** Time-of-day greeting, matching the mockup's 12/18 hour cut-offs. */
export function greetingFor(now: Date): string {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/** The Today eyebrow date, e.g. `"Friday 5 June"`. */
export function longDateLabel(now: Date): string {
  return now.toLocaleDateString(DISPLAY_LOCALE, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}
